// src/components/AnexosGaleria.jsx
//
// Galeria reutilizável de anexos (recibos/comprovantes).
// Aceita o `anexo_url` cru (string única ou JSON array do App) e renderiza
// uma grade de miniaturas. Cada clique chama onOpen(item, index) para abrir
// o item no AnexoViewerModal.
//
// Prévia real: busca o blob de forma AUTENTICADA (mesmo token/endpoint do
// AnexoViewerModal), com lazy-load via IntersectionObserver — só baixa o que
// entra na viewport, importante na tabela do Controle de Gastos. PDFs e tipos
// não-imagem caem num card-ícone; qualquer falha de rede também. O clique
// sempre abre o visualizador completo.

import React, { useState, useEffect, useRef } from 'react';
import {
  FaPaperclip, FaFileAlt, FaFilePdf, FaExpand, FaSpinner,
} from 'react-icons/fa';
import { parseAnexos } from '../utils/parseAnexos';
import { fetchAnexoPreview } from '../utils/anexoClient';
import './AnexosGaleria.css';

function AnexoThumb({ item, index, onOpen, preview }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(!preview);
  // status: 'idle' | 'loading' | 'image' | 'pdf' | 'icon'
  const [estado, setEstado] = useState(preview ? { status: 'idle' } : { status: 'icon' });

  // Lazy-load: só dispara o fetch quando o thumb entra (ou está perto de entrar) na tela
  useEffect(() => {
    if (!preview || visible) return;
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: '250px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [preview, visible]);

  useEffect(() => {
    if (!preview || !visible) return;
    let ativo = true;
    setEstado({ status: 'loading' });
    fetchAnexoPreview(item)
      .then(({ objectUrl, kind }) => {
        if (!ativo) return;
        if (kind === 'image' && objectUrl) setEstado({ status: 'image', src: objectUrl });
        else if (kind === 'pdf')           setEstado({ status: 'pdf' });
        else                               setEstado({ status: 'icon' });
      })
      .catch(() => { if (ativo) setEstado({ status: 'icon' }); });
    return () => { ativo = false; };
  }, [preview, visible, item]);

  const renderInner = () => {
    switch (estado.status) {
      case 'image':
        return (
          <img
            src={estado.src}
            alt={`Anexo ${index + 1}`}
            className="anx-thumb-img"
            draggable={false}
            onError={() => setEstado({ status: 'icon' })}
          />
        );
      case 'pdf':
        return <span className="anx-thumb-fallback pdf"><FaFilePdf /></span>;
      case 'loading':
      case 'idle':
        return <span className="anx-thumb-load"><FaSpinner /></span>;
      case 'icon':
      default:
        return <span className="anx-thumb-fallback"><FaFileAlt /></span>;
    }
  };

  return (
    <button
      ref={ref}
      type="button"
      className="anx-thumb"
      onClick={() => onOpen?.(item, index)}
      title={`Abrir anexo ${index + 1}`}
    >
      {renderInner()}
      <span className="anx-thumb-overlay"><FaExpand /></span>
      <span className="anx-thumb-badge">{index + 1}</span>
    </button>
  );
}

export default function AnexosGaleria({
  anexoUrl,
  onOpen,
  titulo = 'Anexos',
  compact = false,
  preview = true,
}) {
  const anexos = parseAnexos(anexoUrl);
  if (anexos.length === 0) return null;

  return (
    <div className={`anx-galeria ${compact ? 'compact' : ''}`}>
      <div className="anx-galeria-head">
        <FaPaperclip className="anx-galeria-icon" />
        <span className="anx-galeria-title">{titulo}</span>
        <span className="anx-galeria-count">{anexos.length}</span>
      </div>
      <div className="anx-grid">
        {anexos.map((item, i) => (
          <AnexoThumb
            key={`${item}-${i}`}
            item={item}
            index={i}
            onOpen={onOpen}
            preview={preview}
          />
        ))}
      </div>
    </div>
  );
}